import React from 'react';

import './card-usage-options.scss';
import { Form } from '@ampol/design-system';
import { useSelector } from 'react-redux';
import RsaTermsAndConditionsModal from '../rsa-terms-and-conditions-modal/rsa-terms-and-conditions-modal';
import Env from 'Env';
import Vas from 'constants/vas-codes';
import { CardModalFormNames } from '../new-card-modal/types/CardModalFormNames';
import { useSelectAccountDefaults } from 'selectors/selectors';
import {
  filterDailyDollarLimitsByCreditLimit,
  filterRestrictedDailyDollarLimits,
} from 'features/cards/helpers/card-limit-utilities';

interface FleetLimiterOption {
  code: string;
  desc: string;
  available: boolean;
  selected?: boolean;
}

interface DollarLimitOption {
  code: string;
}

interface VehicleTypeOption {
  code: string;
  vehicleType: string;
  roadSideAssistFees?: string;
}

interface CardUsageOptionsProps {
  fleetLimiterOptions?: FleetLimiterOption[];
  dailyDollarLimitsOptions?: DollarLimitOption[];
  monthlyDollarLimitsOptions?: DollarLimitOption[];
  vehicleTypesOptions?: VehicleTypeOption[];
  vasRsaAccountCode?: string;
  vasSnrAccountCode?: string;
  readOnly?: boolean;
  vehicleType?: string;
  vehicleTypes?: VehicleTypeOption[];
  isCardProcessing?: boolean;
  formValues?: Record<string, any>;
  servicesAndRepairsFee?: string;
  roadSideAssistFeeHeavy?: string;
  roadSideAssistFeePassenger?: string;
  roadSideAssistFeeTruck?: string;
  limitMode?: 'add' | 'edit';
  accountCreditLimit?: string | number;
}

/**
 * Describe component here
 */
const CardUsageOptions = ({
  fleetLimiterOptions,
  dailyDollarLimitsOptions,
  monthlyDollarLimitsOptions,
  vehicleTypesOptions,
  vasRsaAccountCode,
  vasSnrAccountCode,
  readOnly,
  vehicleType,
  vehicleTypes,
  isCardProcessing,
  accountCreditLimit,
  limitMode,
}: CardUsageOptionsProps) => {
  const accountDefaultsLoading = useSelector((state: any) => state.accountDefaults.isLoading);
  const shouldFilterDailyLimits = typeof Env.getIsCardLimitsReadOnly === 'function' && Env.getIsCardLimitsReadOnly();
  const allowedDailyDollarLimits = limitMode
    ? filterRestrictedDailyDollarLimits(dailyDollarLimitsOptions)
    : dailyDollarLimitsOptions;

  const dailyDollarLimitsData = (
    shouldFilterDailyLimits
      ? filterDailyDollarLimitsByCreditLimit(allowedDailyDollarLimits, accountCreditLimit)
      : allowedDailyDollarLimits
  )?.map((item) => ({
    label: item.code === '999999999' ? '$999999999 (No Limit)' : `$${item.code}`,
    value: item.code.toString(),
  }));

  const monthlyDollarLimitsData = monthlyDollarLimitsOptions?.map((item) => ({
    label: item.code === '999999999' ? '$999999999 (No Limit)' : `$${item.code}`,
    value: item.code.toString(),
  }));

  const vehicleTypesData = vehicleTypesOptions?.map((item) => ({
    label: item.vehicleType,
    value: item.code,
  }));

  const verificationMethods = [
    { label: 'None', value: 'None' },
    { label: 'PIN', value: 'PIN' },
    { label: 'Signature', value: 'Signature' },
    { label: 'Mandatory PIN', value: 'Mandatory PIN' },
  ];

  const { vasFuelPayAccountCode } = useSelectAccountDefaults();
  const shouldShowRoadsideAssistance = vasRsaAccountCode !== Vas.RSA_NC;
  const shouldShowServiceAndRepairs = true || vasSnrAccountCode !== Vas.SNR_NC;
  const shouldEnableFuelPay = vasFuelPayAccountCode === Vas.FP_NC;

  const getStaticRoadsideAssistFees = () => {
    return vehicleTypes?.find((item) => item.code === vehicleType)?.roadSideAssistFees;
  };

  return (
    <>
      <div className='w-[100%]'></div>
      <div className={'card-usage-options'}>
        <h4>Point of sale (POS) prompts</h4>
        <Form.DropdownSearch
          label={'Verification Method'}
          name={'verifyPosPrompt'}
          data={verificationMethods}
          disabled={readOnly}
          placeholder={'Select Verification Method'}
        />
        <div className={'card-usage-options__pos-checkboxes'}>
          <Form.Checkbox label={'Odometer Required'} name={'odometerRequired'} color={'blue'} disabled={readOnly} />
          <Form.Checkbox label={'Order No Required'} name={'orderNumberRequired'} color={'blue'} disabled={readOnly} />
        </div>
        <h4>Purchase Controls</h4>
        <Form.DropdownSearch
          label={'Monthly Dollar Limit'}
          name={'monthlyLimit'}
          filterOptions={true}
          data={monthlyDollarLimitsData}
          disabled={readOnly}
          loading={accountDefaultsLoading}
        />
        <Form.DropdownSearch
          label={'Daily Dollar Limit'}
          name={'dailyLimit'}
          filterOptions={true}
          data={dailyDollarLimitsData}
          disabled={readOnly}
          loading={accountDefaultsLoading}
        />

        <div className='bg-background-light p-m mt-m'>
          <strong>
            Products and Services * <span>(At least one option is required)</span>
          </strong>
          {fleetLimiterOptions && (
            <Form.Checkbox label={'All Products'} name={'allProducts'} color={'blue'} disabled={readOnly} />
          )}
          <div className={'grid grid-rows-6 grid-flow-col mt-xs gap-xs [&>*]:!m-[0]'}>
            {fleetLimiterOptions?.map((option) => (
              <Form.Checkbox
                key={option.code}
                label={option.desc}
                name={option.code}
                disabled={
                  !option.available ||
                  readOnly ||
                  (Env.getIsEvWay4Enabled() && isCardProcessing && option.code === 'EV')
                }
                color={'blue'}
              />
            ))}
          </div>
        </div>

        {(vasRsaAccountCode !== Vas.RSA_NC || vasSnrAccountCode !== Vas.SNR_NC) && (
          <div className='bg-[#e7e9f5] p-m  mt-m'>
            <strong>Value Added Services</strong>
            <Form.Checkbox
              name={CardModalFormNames.FuelPay}
              label='Enable FuelPay via Ampol app'
              className='!mt-xs'
              color='blue'
              disabled={shouldEnableFuelPay || readOnly}
            />
            <small className='pl-l overflow-hidden block text-sm mt-xs'>
              Link AmpolCard to the Ampol app for seamless payments from your mobile device using FuelPay.
            </small>
            {shouldShowRoadsideAssistance && (
              <>
                <Form.Checkbox
                  name={CardModalFormNames.RoadsideAssistance}
                  label='Roadside Assistance'
                  className='!mt-xs'
                  color='blue'
                  disabled={true}
                />
                <Form.Condition fieldName={CardModalFormNames.RoadsideAssistance} condition={(v) => v === true}>
                  <div className='pl-l'>
                    {vehicleType && <strong>{getStaticRoadsideAssistFees()}</strong>}
                    <Form.DropdownSearch
                      label={'Vehicle Type'}
                      name={CardModalFormNames.VehicleType}
                      filterOptions={true}
                      data={vehicleTypesData}
                      allowClear={false}
                      placeholder={'Select Vehicle Type'}
                      disabled={readOnly}
                    />
                    <RsaTermsAndConditionsModal vehicleTypeSelected={vehicleType} readOnly={readOnly} />
                  </div>
                </Form.Condition>
              </>
            )}
            {shouldShowServiceAndRepairs && (
              <>
                <Form.Checkbox
                  name={CardModalFormNames.ServiceAndRepairs}
                  label='Services and Repairs'
                  className='!mt-xs'
                  color='blue'
                  disabled={true}
                />
                <Form.Condition fieldName={CardModalFormNames.ServiceAndRepairs} condition={(v) => v === true}>
                  <strong className='ml-l mt-xs block'>$2/mth</strong>
                </Form.Condition>
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default CardUsageOptions;
